import React, { useState } from 'react';
import './corporate-action-payment-report-container.scss';
import UploadModalContent from '../modal/upload-modal/upload-modal';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import ReportsService from '../../../../../../services/reports-service';
import { ExcelPreviewModal } from '../modal/excel-preview-modal/excel-preview-modal';
import * as XLSX from 'xlsx';

const MAX_PREVIEW_ROWS = 500; // limit rows for performance

const CorporateActionPaymentReportContainer = ({ filteredReports = [], handleCheckboxChange }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');

  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false);
  const [previewLoadingMap, setPreviewLoadingMap] = useState({});

  // Error modal state for preview/download errors
  const [errorModalShow, setErrorModalShow] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const reportService = new ReportsService();

  const handleCommentClick = (comment) => {
    setSelectedComment(comment);
    setShowCommentModal(true);
  };

  const handleUploadClick = (report) => {
    setSelectedReport(report);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedReport(null);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedReports = [...filteredReports].sort((a, b) => {
    if (sortConfig.key) {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'paymentDate' || sortConfig.key === 'dateSubmitted') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
    }
    return 0;
  });

  // Helpers: detect Excel by extension
  const isExcelByExtension = (fileURL) => {
    try {
      const urlObj = new URL(fileURL);
      const path = urlObj.pathname.toLowerCase();
      return path.endsWith('.xlsx') || path.endsWith('.xls');
    } catch {
      const lower = fileURL.split('?')[0].toLowerCase();
      return lower.endsWith('.xlsx') || lower.endsWith('.xls');
    }
  };

  // Fetch and parse workbook
  const fetchWorkbook = async (fileURL) => {
    const fileResponse = await fetch(fileURL);
    if (!fileResponse.ok) {
      throw new Error(`Network response was not ok (${fileResponse.status})`);
    }
    const blob = await fileResponse.blob();
    const arrayBuffer = await blob.arrayBuffer();
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('No sheets found in workbook');
      }
      return workbook;
    } catch (parseErr) {
      console.error('Error parsing Excel:', parseErr);
      throw new Error('Failed to parse Excel file');
    }
  };

  // Normalize sheet into 2D array with limit
  const normalizeSheet = (sheet) => {
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const totalRows = jsonData.length;
    let usedRows = jsonData;
    if (totalRows > MAX_PREVIEW_ROWS) {
      usedRows = jsonData.slice(0, MAX_PREVIEW_ROWS);
      console.warn(`Preview limited to first ${MAX_PREVIEW_ROWS} rows out of ${totalRows}`);
    }
    const maxCols = usedRows.reduce((max, row) => Math.max(max, row.length), 0);
    const normalizedData = usedRows.map(row =>
      Array.from({ length: maxCols }, (_, i) => (row[i] !== undefined ? row[i] : ''))
    );
    return normalizedData.map(row =>
      row.map(cell => ({ value: cell, readOnly: true }))
    );
  };

  // Helper for Office Web Viewer embed URL if fallback
  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
      if (pathname.endsWith('.xlsx') || pathname.endsWith('.xls')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    } catch (e) {
      const strippedUrl = url.split('?')[0].toLowerCase();
      if (strippedUrl.endsWith('.xlsx') || strippedUrl.endsWith('.xls')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    }
    return url;
  };

  const showError = (message) => {
    setErrorModalMessage(message);
    setErrorModalShow(true);
  };

  const handlePreviewClick = async (report) => {
    const key = report.id;
    setPreviewLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      const response = await reportService.retrieveReport(
        report.type, report.isin, report.title, report.paymentDate
      );
      const fileURL = response.fileURL;
      if (!fileURL) {
        throw new Error('No file URL returned');
      }

      if (isExcelByExtension(fileURL)) {
        try {
          const workbook = await fetchWorkbook(fileURL);
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const data = normalizeSheet(sheet);
          setExcelPreviewData(data);
          setShowExcelPreviewModal(true);
        } catch (parseErr) {
          console.error('Excel fetch/parse failed, falling back to open:', parseErr);
          const embedUrl = getEmbedUrl(fileURL);
          const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
          if (newWindow) newWindow.opener = null;
        }
      } else {
        const embedUrl = getEmbedUrl(fileURL);
        const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error fetching report preview:', error.message || error);
      showError('Failed to load preview: ' + (error.message || error));
    } finally {
      setPreviewLoadingMap(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleDownloadClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.type, report.isin, report.title, report.paymentDate
      );
      const fileURL = response.fileURL;
      if (!fileURL) throw new Error('No file URL returned');
      const fileResponse = await fetch(fileURL);
      if (!fileResponse.ok) throw new Error('Network response was not ok');
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = response.fileName || 'download.pdf';
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading report:', error.message || error);
      showError('Failed to download file: ' + (error.message || error));
    }
  };

  return (
    <div className="table-container">
      <table className="report-table">
        <thead>
          <tr>
            <th className="checkbox-header"></th>
            <th>Report Title</th>
            <th>Issuer</th>
            <th>ISIN</th>
            <th onClick={() => handleSort('paymentDate')}>
              Payment Date{' '}
              {sortConfig.key === 'paymentDate' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th>Status</th>
            <th onClick={() => handleSort('dateSubmitted')}>
              Date Submitted{' '}
              {sortConfig.key === 'dateSubmitted' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th>Issuer Receipt Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedReports.map(report => {
            const isLoading = !!previewLoadingMap[report.id];
            return (
              <tr key={report.id}>
                <td className="checkbox-cell">
                  {report.status === 'For Checking' ? (
                    <input
                      type="checkbox"
                      onChange={() => handleCheckboxChange(report.id)}
                    />
                  ) : null}
                </td>
                <td>{report.title}</td>
                <td>{report.issuer}</td>
                <td>{report.isin}</td>
                <td>{report.paymentDate}</td>
                <td className={`status ${report.status.replace(' ', '-').toLowerCase()}`}>
                  {report.status}
                  {report.comment && (
                    <span
                      style={{ cursor: 'pointer', marginLeft: '5px' }}
                      onClick={() => handleCommentClick(report.comment)}
                    >
                      &#9865;
                    </span>
                  )}
                </td>
                <td>{report.dateSubmitted}</td>
                <td>{report.issuerReceiptDate}</td>
                <td>
                  <div className="action-buttons">
                    {report.status === 'For Checking' && (
                      <Button
                        variant="primary"
                        className="action-button"
                        onClick={() => handleUploadClick(report)}
                      >
                        Upload
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      className="action-button"
                      onClick={() => handlePreviewClick(report)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          /> Previewing...
                        </>
                      ) : (
                        'Preview'
                      )}
                    </Button>
                    <Button
                      variant="primary"
                      className="action-button"
                      onClick={() => handleDownloadClick(report)}
                    >
                      Download
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Upload Modal */}
      {selectedReport && (
        <Modal
          show={showUploadModal}
          onHide={handleCloseModal}
          dialogClassName="checker-upload-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Upload Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <UploadModalContent
              context={{ report: selectedReport }}
              onClose={handleCloseModal}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Comment Modal */}
      <Modal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        backdrop="static"
        dialogClassName="commentModal modal-dialog-centered"
      >
        <Modal.Header closeButton>
          <Modal.Title>Report Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{selectedComment || 'No Comments'}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => {
            setShowExcelPreviewModal(false);
            setExcelPreviewData([]);
          }}
        />
      )}
    </div>
  );
};

export default CorporateActionPaymentReportContainer;
